import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import PeriodPicker, { DEFAULT_PERIOD, periodParams } from "@/components/PeriodPicker";
import { Tags, PlugZap, ChevronDown, ChevronUp, UserRound, AlertTriangle, Trophy } from "lucide-react";

// Colore per etichetta: verde = buona, rossa = cattiva, gialla = neutra.
export function labelTone(label) {
  const l = (label || "").toLowerCase();
  if (/(negativ|fuori target|concorrenza|finti|non risponde|mai risposto|poco interessato|seriale|dati finti)/.test(l)) return "red";
  if (/(contratto|pagamento|molto interessato|venduto)/.test(l) || l === "interessato") return "green";
  return "amber";
}

const CHIP = {
  green: "bg-emerald-100 text-emerald-800 border-emerald-200",
  amber: "bg-amber-100 text-amber-800 border-amber-200",
  red: "bg-rose-100 text-rose-700 border-rose-200",
};

export function LabelChip({ label, count, testid }) {
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${CHIP[labelTone(label)]}`}>
      <strong className="text-sm">{count}</strong> {label}
    </span>
  );
}

export default function QualityLabels() {
  const [period, setPeriod] = useState(DEFAULT_PERIOD);
  const { data, isLoading } = useQuery({
    queryKey: ["analytics-labels", period.key || period.mode],
    queryFn: async () => (await api.get("/analytics/labels", { params: periodParams(period) })).data,
  });

  return (
    <div className="space-y-6" data-testid="labels-page">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight">Qualità dei lead</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          L'<strong>ultima etichetta</strong> che i commerciali mettono su Pipedrive dopo l'arrivo di ogni lead.
          Verde = buona, rossa = cattiva, gialla = in sospeso. L'etichetta automatica "Ha richiesto info" non viene contata.
        </p>
      </div>

      <PeriodPicker value={period} onChange={setPeriod} testPrefix="labels-period" />

      {isLoading ? (
        <>
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-48 w-full" />
        </>
      ) : (
        <LabelsContent data={data} periodLabel={period.label} />
      )}
    </div>
  );
}

function LabelsContent({ data, periodLabel }) {
  if (!data?.pipedrive_connected) {
    return (
      <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="labels-pipedrive-banner">
        <PlugZap className="mt-0.5 h-5 w-5 flex-shrink-0" />
        <p>Collega Pipedrive nella pagina Integrazioni per vedere le etichette dei lead.</p>
      </div>
    );
  }
  return (
    <>
      <QualityAlert alerts={data.quality_alerts || []} />
      <LabelsOverview data={data} periodLabel={periodLabel} />
      <CampaignScores scores={data.campaign_scores || []} periodLabel={periodLabel} />
      <CommercialLabels commercials={data.commercials || []} periodLabel={periodLabel} />
      <CampaignLabels campaigns={data.campaigns || []} periodLabel={periodLabel} />
    </>
  );
}

function LabelsOverview({ data, periodLabel }) {
  const pct = data.total ? Math.round((data.labeled / data.total) * 100) : 0;
  return (
    <Card className="shadow-sm" data-testid="labels-overview">
      <CardContent className="p-6">
        <div className="flex items-center gap-2">
          <Tags className="h-5 w-5 text-slate-600" />
          <p className="text-sm font-bold">Come stanno andando i lead <span className="font-normal text-muted-foreground">({periodLabel})</span></p>
        </div>
        <p className="mt-2 text-sm text-muted-foreground">
          Su <strong>{data.total}</strong> lead, <strong>{data.labeled}</strong> hanno già un'etichetta ({pct}%).{" "}
          {data.no_label > 0 && <>Gli altri <strong>{data.no_label}</strong> non sono ancora stati etichettati.</>}
        </p>
        {data.overall.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">Nessuna etichetta in questo periodo</p>
        ) : (
          <div className="mt-4 flex flex-wrap gap-2" data-testid="overall-labels">
            {data.overall.map((o) => (
              <LabelChip key={o.label} label={o.label} count={o.count} testid="overall-label-chip" />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function CommercialLabels({ commercials, periodLabel }) {
  return (
    <Card className="border-2 border-blue-200 shadow-sm" data-testid="commercial-labels">
      <CardContent className="p-6">
        <div className="mb-1 flex items-center gap-2">
          <UserRound className="h-5 w-5 text-blue-600" />
          <p className="text-sm font-bold">Etichette per commerciale</p>
        </div>
        <p className="mb-4 text-xs text-muted-foreground">
          Quali etichette ha usato ogni commerciale sui suoi lead ({periodLabel}). Il grigio conta i lead lasciati <strong>senza etichetta</strong>.
        </p>
        {commercials.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">Nessun lead assegnato ai commerciali in questo periodo</p>
        ) : (
          <div className="space-y-3">
            {commercials.map((c) => (
              <div key={c.name} data-testid="commercial-labels-row" className="rounded-lg border border-border bg-secondary/30 p-4">
                <div className="flex items-center gap-3">
                  <span className="min-w-0 flex-1 truncate text-sm font-semibold">{c.name}</span>
                  <span className="flex-shrink-0 text-xs text-muted-foreground">{c.labeled}/{c.leads} lead etichettati</span>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {c.labels.map((l) => (
                    <LabelChip key={l.label} label={l.label} count={l.count} testid="commercial-label-chip" />
                  ))}
                  {c.no_label > 0 && (
                    <span data-testid="commercial-no-label-chip" className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">
                      <strong className="text-sm">{c.no_label}</strong> nessuna etichetta assegnata
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function CampaignLabels({ campaigns, periodLabel }) {
  const [showAll, setShowAll] = useState(false);
  const withLabels = campaigns.filter((c) => c.labeled > 0);
  const noLabels = campaigns.filter((c) => c.labeled === 0);
  return (
    <Card className="shadow-sm" data-testid="campaign-labels">
      <CardContent className="p-6">
        <p className="mb-1 text-sm font-bold">Etichette per campagna</p>
        <p className="mb-4 text-xs text-muted-foreground">
          Cosa hanno detto i commerciali dei lead di ogni campagna ({periodLabel}). In alto le campagne con più lead etichettati.
        </p>
        {withLabels.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">Nessuna campagna con lead etichettati in questo periodo</p>
        ) : (
          <div className="space-y-3">
            {withLabels.map((c) => (
              <div key={c.name} data-testid="campaign-labels-row" className="rounded-lg border border-border bg-secondary/30 p-4">
                <div className="flex items-center gap-3">
                  <span className="min-w-0 flex-1 truncate text-sm font-semibold">{c.name}</span>
                  <span className="flex-shrink-0 text-xs text-muted-foreground">{c.labeled}/{c.leads} lead etichettati</span>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {c.labels.map((l) => (
                    <LabelChip key={l.label} label={l.label} count={l.count} testid="campaign-label-chip" />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
        {noLabels.length > 0 && (
          <div className="mt-4">
            <button
              data-testid="toggle-unlabeled-campaigns"
              onClick={() => setShowAll((v) => !v)}
              className="flex w-full items-center justify-between rounded-lg border border-border bg-secondary/40 px-3 py-2.5 text-sm font-semibold text-muted-foreground transition-colors hover:bg-secondary"
            >
              <span>{noLabels.length} campagne senza lead etichettati</span>
              {showAll ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
            {showAll && (
              <div className="mt-2 space-y-1.5">
                {noLabels.map((c) => (
                  <div key={c.name} data-testid="unlabeled-campaign-row" className="flex items-center gap-3 rounded-lg border border-border bg-secondary/20 px-3 py-1.5">
                    <span className="min-w-0 flex-1 truncate text-sm">{c.name}</span>
                    <span className="text-xs text-muted-foreground">{c.leads} lead</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function QualityAlert({ alerts }) {
  if (!alerts || alerts.length === 0) return null;
  return (
    <Card className="border-2 border-rose-300 bg-rose-50 shadow-sm" data-testid="quality-alert">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 text-rose-800">
          <AlertTriangle className="h-5 w-5" />
          <p className="text-sm font-bold">Attenzione: campagne con troppi lead finti o fuori target</p>
        </div>
        <p className="mt-1 text-xs text-rose-700">
          Sono i lead peggiori in assoluto. Controlla subito il targeting di queste campagne.
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          {alerts.map((a) => (
            <span key={a.name} data-testid="quality-alert-chip" className="inline-flex items-center gap-2 rounded-full border border-rose-300 bg-white px-3 py-1.5 text-xs font-semibold text-rose-700">
              <span className="max-w-[220px] truncate" title={a.name}>{a.name}</span>
              <span className="rounded-full bg-rose-100 px-2 py-0.5">{a.junk} finti/fuori target · {a.junk_pct}%</span>
            </span>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

const GRADE_TONE = {
  A: "bg-emerald-100 text-emerald-800 border-emerald-300",
  B: "bg-lime-100 text-lime-800 border-lime-300",
  C: "bg-amber-100 text-amber-800 border-amber-300",
  D: "bg-rose-100 text-rose-700 border-rose-300",
};

function CampaignScores({ scores, periodLabel }) {
  if (!scores || scores.length === 0) return null;
  return (
    <Card className="border-2 border-violet-200 shadow-sm" data-testid="campaign-scores">
      <CardContent className="p-6">
        <div className="mb-1 flex items-center gap-2">
          <Trophy className="h-5 w-5 text-violet-600" />
          <p className="text-sm font-bold">Punteggio qualità campagne</p>
        </div>
        <p className="mb-4 text-xs text-muted-foreground">
          Voto da 0 a 100 = <strong>30% qualità etichette</strong> + <strong>70% conversione finale</strong> ({periodLabel}).
          Solo campagne con almeno 5 lead. Dalla migliore alla peggiore.
        </p>
        <div className="space-y-2">
          {scores.map((c) => (
            <div key={c.name} data-testid="campaign-score-row" className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-secondary/30 p-3">
              <span className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full border text-sm font-bold ${GRADE_TONE[c.grade]}`}>{c.grade}</span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold" title={c.name}>{c.name}</p>
                <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-slate-200">
                  <div className="h-full rounded-full bg-violet-500" style={{ width: `${c.score}%` }} />
                </div>
              </div>
              <div className="flex flex-shrink-0 flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                <span className="text-sm font-bold text-violet-700">{c.score}</span>
                <span className="text-muted-foreground">conv. {c.conversion_pct}% ({c.converted}/{c.leads})</span>
                {c.good > 0 && <span className="font-semibold text-emerald-700">{c.good} buone</span>}
                {c.bad > 0 && <span className="font-semibold text-rose-700">{c.bad} cattive</span>}
                {c.junk > 0 && <span className="rounded-full bg-rose-100 px-2 py-0.5 font-semibold text-rose-700">{c.junk} finti/fuori target</span>}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
